import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, h1, h2, sub, btn, RGB, Icon, ICONS, errMsg } from './extUi';
import MatchSeatingEditor, { seatingComplete } from './stage/MatchSeatingEditor';

// "Edit seating" of one match: sides + seats, starting from the match's own copy of the players.
// Only this match changes; the teams' default seating stays as it is. "Reload players from the
// teams" re-copies the teams' current players (and their default seating).
// Route: /admin/tournaments/:id/matches/:mid/seating

const poolOf = (side) => [...side.seats.map(s => ({ steam_id: s.steam_id, ign: s.ign })), ...side.substitutes];
const fromTeam = (tm) => ({ name: tm.name, players: tm.players.map(p => ({ steam_id: p.steam_id, ign: p.ign })), defaults: tm.seating.seats.map(s => s.steam_id) });
const fromMatch = (s) => ({
  teams: {
    team1: { name: s.team1.name, players: poolOf(s.team1), defaults: null },
    team2: { name: s.team2.name, players: poolOf(s.team2), defaults: null },
  },
  value: { team1_stage_side: s.team1.stage_side, team1: { seats: s.team1.seats.map(x => x.steam_id) }, team2: { seats: s.team2.seats.map(x => x.steam_id) } },
});

export default function ExtMatchSeating() {
  const { t } = useTranslation();
  const { id, mid } = useParams();
  const [orig, setOrig] = useState(null);
  const [teams, setTeams] = useState(null);
  const [value, setValue] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadMatchCopy = (s) => { const x = fromMatch(s); setTeams(x.teams); setValue(x.value); setRefresh(false); };

  useEffect(() => {
    axios.get(`/api/v1/ext/tournaments/${id}/matches/${mid}/seating`).then(r => { const x = fromMatch(r.data); setOrig(r.data); setTeams(x.teams); setValue(x.value); })
      .catch(e => setMsg({ error: true, text: errMsg(e, t('ext.loadFailed', 'Failed to load')) }));
  }, [id, mid, t]);

  const reloadFromTeams = async () => {
    setMsg(null);
    try {
      const [a, b] = await Promise.all([axios.get(`/api/v1/ext/tournaments/${id}/teams/${orig.team1.team_id}`), axios.get(`/api/v1/ext/tournaments/${id}/teams/${orig.team2.team_id}`)]);
      const t1 = fromTeam(a.data), t2 = fromTeam(b.data);
      setTeams({ team1: t1, team2: t2 });
      setValue(v => ({ ...v, team1: { seats: t1.defaults }, team2: { seats: t2.defaults } }));
      setRefresh(true);
    } catch (e) { setMsg({ error: true, text: errMsg(e, t('ext.loadFailed', 'Failed to load')) }); }
  };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await axios.put(`/api/v1/ext/tournaments/${id}/matches/${mid}/seating`, { ...value, refresh_players: refresh });
      setOrig(r.data); loadMatchCopy(r.data);
      setMsg({ text: t('extSeating.matchSaved', 'Seating saved for this match') });
    } catch (e) { setMsg({ error: true, text: errMsg(e, t('ext.saveFailed', 'Could not save')) }); }
    finally { setBusy(false); }
  };

  if (!value) return <div style={sub}>{msg ? msg.text : t('common.loading', 'Loading...')}</div>;
  const ok = seatingComplete(value);

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '1.25rem', paddingBottom: '0.75rem' }}>
        <Link to={`/admin/tournaments/${id}/matches/${mid}`} style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Icon d={ICONS.back} /> {t('extSeating.backToMatch', 'Back to the match')}</Link>
        <h1 style={{ ...h1, marginTop: '0.35rem' }}>{t('extSeating.matchTitle', 'Stage seating · {{a}} vs {{b}}', { a: orig.team1.name, b: orig.team2.name })}</h1>
      </div>
      <div style={card}>
        <h2 style={h2}>{t('extSeating.matchSeating', 'Seating for this match')}</h2>
        <p style={sub}>{t('extSeating.matchSeatingHint', 'Seen from the crowd. Changes apply to this match only; the teams’ default seating is not changed. Display boards get the update on the API streams right away.')}</p>
        <MatchSeatingEditor teams={teams} value={value} onChange={setValue} />
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginTop: '0.9rem', flexWrap: 'wrap' }}>
          {msg && <span style={{ fontSize: '0.8rem', color: msg.error ? 'var(--accent-red)' : 'var(--accent-green)' }}>{msg.text}</span>}
          <span style={{ flex: 1 }} />
          {refresh
            ? <button type="button" style={btn(RGB.gray)} onClick={() => loadMatchCopy(orig)}>{t('extSeating.useMatchCopy', 'Back to this match’s players')}</button>
            : <button type="button" style={btn(RGB.amber)} onClick={reloadFromTeams} title={t('extSeating.reloadTeamsHint', 'Use the teams’ current players and default seating (e.g. after a roster change)')}><Icon d={ICONS.refresh} /> {t('extSeating.reloadTeams', 'Reload players from the teams')}</button>}
          <button type="button" disabled={busy || !ok} style={{ ...btn(RGB.green, true), opacity: ok ? 1 : 0.5 }} onClick={save}>{busy ? t('common.loading', 'Loading...') : t('extSeating.saveSeating', 'Save seating')}</button>
        </div>
      </div>
    </div>
  );
}
